import {
  transport,
  useTransportPaginatedQuery,
  useTransportQuery,
} from "@/transport"

import type { GetAllAnnotationsByOrgRequest } from "@/types_api/backend/transport/get_all_annotations_by_org_request"
import type { GetAnnotationsWithMetricOutputsRequest } from "@/types_api/backend/transport/get_annotations_with_metric_outputs_request"
import type { GetAnnotationsWithMetricOutputsResponse } from "@/types_api/backend/transport/get_annotations_with_metric_outputs_response"
import type { GetFeedbackForSimulationOutputRequest } from "@/types_api/backend/transport/get_feedback_for_simulation_output_request"
import type { ReviewAnnotationsByMetricResponse } from "@/types_api/backend/transport/review_annotations_by_metric_response"
import type { Annotation, AnnotationDetails } from "@/types/api/Annotation"
import type { MetricOutputsByMetric } from "@/types/api/MetricOutputsByMetric"
import type { JsonObject } from "@/types/json"

const FEEDBACK_CACHE_TIME_MS = 60_000
const ANNOTATIONS_WITH_METRIC_OUTPUTS_PAGE_SIZE = 1_000
const ANNOTATIONS_WITH_METRIC_OUTPUTS_MAX_PAGES = 100

interface AnnotationsWithMetricOutputs {
  annotations: Record<string, Annotation[]>
  metricOutputs: MetricOutputsByMetric
  audioLengthsBySimulationOutputId: Record<string, number | null>
}

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value)

export const mapReviewAnnotationResource = (
  annotation: GetAnnotationsWithMetricOutputsResponse["annotations"][string][number],
): Annotation => ({
  id: annotation.id,
  project_annotation_id: annotation.project_annotation_id ?? null,
  simulation_output_id: annotation.simulation_output_id,
  assignee: annotation.assignee,
  metric_id: annotation.metric_id,
  ground_truth_float_value: annotation.ground_truth_float_value ?? null,
  ground_truth_string_value: annotation.ground_truth_string_value ?? null,
  ground_truth_set_value: annotation.ground_truth_set_value ?? null,
  ground_truth_json: isJsonObject(annotation.ground_truth_json)
    ? annotation.ground_truth_json
    : null,
  ground_truth_subvalues_by_timestamp:
    annotation.ground_truth_subvalues_by_timestamp ?? null,
  annotations: isJsonObject(annotation.annotations)
    ? (annotation.annotations as AnnotationDetails)
    : annotation.reviewer_notes
      ? { custom_explanation: annotation.reviewer_notes }
      : null,
  archived: annotation.status === "ARCHIVED",
  created_at: annotation.create_time,
  last_updated_at: annotation.update_time,
  priority:
    annotation.priority === "PRIORITY_PRIMARY" ||
    annotation.priority === "PRIMARY"
      ? "PRIMARY"
      : "STANDARD",
})

export function mapAnnotationsWithMetricOutputsResponse(
  response: GetAnnotationsWithMetricOutputsResponse,
): AnnotationsWithMetricOutputs {
  const annotations = Object.fromEntries(
    Object.entries(response.annotations).map(([metricId, rows]) => [
      metricId,
      rows.map(mapReviewAnnotationResource),
    ]),
  )

  const metricOutputs: MetricOutputsByMetric = Object.fromEntries(
    Object.entries(response.metric_outputs).map(
      ([metricId, outputsBySimulation]) => [
        metricId,
        Object.fromEntries(
          Object.entries(outputsBySimulation).map(
            ([simulationOutputId, output]) => [
              simulationOutputId,
              {
                output_type: output.output_type ?? "unknown",
                float_value: output.float_value ?? null,
                string_value: output.string_value ?? null,
                set_value: output.set_value ?? null,
                error_status: output.error_status ?? null,
                created_at: output.created_at,
                explanation: output.explanation ?? null,
                result: isJsonObject(output.result) ? output.result : null,
                result_json: isJsonObject(output.result_json)
                  ? output.result_json
                  : null,
                subvalues_by_timestamp: Array.isArray(
                  output.subvalues_by_timestamp,
                )
                  ? output.subvalues_by_timestamp
                  : null,
                metric_version_number: output.metric_version_number ?? null,
                metric_version_ulid: output.metric_version_ulid ?? null,
              },
            ],
          ),
        ),
      ],
    ),
  )

  return {
    annotations,
    metricOutputs,
    audioLengthsBySimulationOutputId:
      response.audio_lengths_by_simulation_output_id,
  }
}

function combineAnnotationsWithMetricOutputsPages(
  pages: GetAnnotationsWithMetricOutputsResponse[],
): GetAnnotationsWithMetricOutputsResponse {
  const annotations: GetAnnotationsWithMetricOutputsResponse["annotations"] = {}
  const metricOutputs: GetAnnotationsWithMetricOutputsResponse["metric_outputs"] =
    {}
  const audioLengths: GetAnnotationsWithMetricOutputsResponse["audio_lengths_by_simulation_output_id"] =
    {}

  for (const page of pages) {
    for (const [metricId, rows] of Object.entries(page.annotations)) {
      annotations[metricId] = [...(annotations[metricId] ?? []), ...rows]
    }

    for (const [metricId, outputsBySimulation] of Object.entries(
      page.metric_outputs,
    )) {
      metricOutputs[metricId] = {
        ...(metricOutputs[metricId] ?? {}),
        ...outputsBySimulation,
      }
    }

    Object.assign(audioLengths, page.audio_lengths_by_simulation_output_id)
  }

  return {
    annotations,
    metric_outputs: metricOutputs,
    audio_lengths_by_simulation_output_id: audioLengths,
    next_page_token: pages[pages.length - 1]?.next_page_token ?? null,
  }
}

function countAnnotationsWithMetricOutputsPageItems(
  page: GetAnnotationsWithMetricOutputsResponse,
): number {
  return Object.values(page.annotations).reduce(
    (total, rows) => total + rows.length,
    0,
  )
}

function getNextAnnotationsWithMetricOutputsRequest(
  page: GetAnnotationsWithMetricOutputsResponse,
  previousRequest: GetAnnotationsWithMetricOutputsRequest | undefined,
): GetAnnotationsWithMetricOutputsRequest | null {
  if (!page.next_page_token || !previousRequest) {
    return null
  }

  return {
    ...previousRequest,
    page_token: page.next_page_token,
  }
}

function mapReviewAnnotationsByMetricResponse(
  response: ReviewAnnotationsByMetricResponse,
): Record<string, Annotation[]> {
  return Object.fromEntries(
    Object.entries(response.annotations_by_metric_id).map(
      ([metricId, rows]) => [metricId, rows.map(mapReviewAnnotationResource)],
    ),
  )
}

export function useFeedbackForSimulationOutputTransport({
  simulationOutputId,
  projectId,
  filterArchived,
  enabled = true,
  gcTime,
  refetchInterval,
  refetchOnWindowFocus,
  staleTime,
}: {
  simulationOutputId: string
  projectId?: string | null
  filterArchived?: boolean
  enabled?: boolean
  gcTime?: number
  refetchInterval?: number | false
  refetchOnWindowFocus?: boolean
  staleTime?: number
}) {
  const request: GetFeedbackForSimulationOutputRequest = {
    simulation_output_id: simulationOutputId,
    ...(projectId ? { project_id: projectId } : {}),
    filter_archived: filterArchived,
  }

  return useTransportQuery({
    operation: transport.operations.getFeedbackForSimulationOutput,
    request,
    options: {
      enabled: !!simulationOutputId && enabled,
      gcTime: gcTime ?? FEEDBACK_CACHE_TIME_MS,
      refetchInterval,
      refetchOnWindowFocus,
      select: mapReviewAnnotationsByMetricResponse,
      staleTime,
    },
  })
}

export function useAllAnnotationsByOrgTransport({
  metricId,
  filterArchived,
  enabled = true,
}: {
  metricId?: string | null
  filterArchived?: boolean
  enabled?: boolean
} = {}) {
  const request: GetAllAnnotationsByOrgRequest = {
    metric_id: metricId,
    filter_archived: filterArchived,
  }

  return useTransportQuery({
    operation: transport.operations.getAllAnnotationsByOrg,
    request,
    options: {
      enabled,
      select: mapReviewAnnotationsByMetricResponse,
    },
  })
}

export function useAnnotationsWithMetricOutputsTransport({
  metricId,
  metricIds,
  simulationOutputIds,
  projectIds,
  filterArchived,
  includeMetricOutputDetails,
  includeSubvalues,
  includeAudioLengths,
  enabled = true,
}: {
  metricId?: string
  metricIds?: string[]
  simulationOutputIds?: string[]
  projectIds?: string[]
  filterArchived?: boolean
  includeMetricOutputDetails?: boolean
  includeSubvalues?: boolean
  includeAudioLengths?: boolean
  enabled?: boolean
}) {
  const request: GetAnnotationsWithMetricOutputsRequest = {
    metric_id: metricId,
    metric_ids: metricIds,
    simulation_output_ids: simulationOutputIds,
    project_ids: projectIds,
    filter_archived: filterArchived,
    include_metric_output_details: includeMetricOutputDetails,
    include_subvalues: includeSubvalues,
    include_audio_lengths: includeAudioLengths,
    page_size: ANNOTATIONS_WITH_METRIC_OUTPUTS_PAGE_SIZE,
  }

  const hasMetricScope =
    !!metricId || !!metricIds?.length || !!projectIds?.length
  return useTransportPaginatedQuery({
    combinePages: (pages) =>
      mapAnnotationsWithMetricOutputsResponse(
        combineAnnotationsWithMetricOutputsPages(pages),
      ),
    getNextRequest: getNextAnnotationsWithMetricOutputsRequest,
    getPageItemCount: countAnnotationsWithMetricOutputsPageItems,
    maxPages: ANNOTATIONS_WITH_METRIC_OUTPUTS_MAX_PAGES,
    operation: transport.operations.getAnnotationsWithMetricOutputs,
    request,
    options: {
      enabled: hasMetricScope && enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  })
}
